import { usePrevious } from '@dnd-kit/utilities';
import { Button, Divider, Grid, Group, SegmentedControl, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy, IconExternalLink, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import CrossDomainCopy from '../CrossDomainCopy';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import Rules from '../Rules/Rules';
import AudienceRules from './AudienceRules';

import behavioralRuleFilters from '../../constants/Rules/behaviorRuleFilters';
import behavioralRules from '../../constants/Rules/behaviorSegmentRules';

import profileFilterRuleFilters from '../../constants/Rules/profileFilterRuleFilters';
import profileFilterRules from '../../constants/Rules/profileFilterSegmentRules';
import SegmentCounter from './SegmentCounter';

const EXTERNAL_SCOPES = ['facebook', 'google'];

const COUNT_SCOPES = [
  { label: 'Opt-in Marketing', value: 'marketing' },
  { label: 'Opt-out Marketing', value: 'optOut' },
  { label: 'Transactional', value: 'transactional' },
];

const SegmentForm = ({
  segment: passedSegment,
  saveSegment,
  deleteSegment,
  onBack,
  onDuplicate,
  disabledScopes = [],
}) => {
  const fetch = useFetch();

  const [segment, setSegment] = useState(passedSegment);
  const [customFields, setCustomFields] = useState();
  const [campaigns, setCampaigns] = useState();
  const [emails, setEmails] = useState();
  const [eventTags, setEventTags] = useState();
  const [eventActions, setEventActions] = useState();
  const [textMessages, setTextMessages] = useState();
  const [viberMessages, setViberMessages] = useState();
  const [webhooks, setWebhooks] = useState();
  const [pushNotifications, setPushNotifications] = useState();
  const [subscriptions, setSubscriptions] = useState();
  const [pages, setPages] = useState();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [error, setError] = useState();
  const [deleteError, setDeleteError] = useState();
  const [segments, setSegments] = useState();
  const [whatsappMessages, setWhatsappMessages] = useState();
  const [surveys, setSurveys] = useState();
  const [facebookTos, setFacebookTos] = useState({ accepted: false, webCustomAudienceAccepted: false });
  const [facebookTosLoading, setFacebookTosLoading] = useState(false);
  const [countScope, setCountScope] = useState('transactional');

  const { accountId, domainId } = useParams();
  const facebook = useSelector((state) => state.facebook);

  const previousSegment = usePrevious(passedSegment);
  const saveDisabled = () => {
    const { name, scope, rules = [], externalScope } = segment;

    if (!name) return true;
    if (['facebook', 'google'].includes(scope) && rules.length === 0) return true;

    const isLookalike = externalScope === 'audienceLookalikes';
    const isPageLikeLookalike = externalScope === 'pageLikeLookalikes';

    if (isLookalike && !rules[0]?.lookalikeType) return true;

    if (scope === 'facebook' && isLookalike && !rules[0]?.originAudienceId) return true;

    if (isPageLikeLookalike && !rules[0]?.lookalikeType) return true;

    if (scope === 'facebook' && facebook?.user?.adAccountId && !facebookTosLoading) {
      if (externalScope === 'audienceRules' && !facebookTos.webCustomAudienceAccepted) return true;
      if ((isLookalike || isPageLikeLookalike) && !facebookTos.accepted) return true;
    }

    return false;
  };

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
      .then(({ customFields }) => setCustomFields(customFields))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emails`)
      .then(({ emails }) => setEmails(emails))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pushNotifications`)
      .then(({ pushNotifications }) => setPushNotifications(pushNotifications))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/textMessages`)
      .then(({ textMessages }) => setTextMessages(textMessages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages`)
      .then(({ viberMessages }) => setViberMessages(viberMessages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns`)
      .then(({ campaigns }) => setCampaigns(campaigns))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventTags`)
      .then(({ eventTags }) => setEventTags(eventTags))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages`)
      .then(({ pages }) => setPages(pages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/subscriptions`)
      .then(({ subscriptions }) => setSubscriptions(subscriptions))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments`)
      .then(({ segments }) => setSegments(segments))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventActions`)
      .then(({ eventActions }) => {
        setEventActions(eventActions);
      })
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/whatsappMessages`)
      .then(({ whatsappMessages }) => setWhatsappMessages(whatsappMessages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/webhooks`)
      .then(({ webhooks }) => setWebhooks(webhooks))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/surveys`)
      .then(({ surveys }) => setSurveys(surveys))
      .catch(setError);
  }, [accountId, domainId, fetch]);

  useEffect(() => {
    setFacebookTos({ accepted: false, webCustomAudienceAccepted: false });

    if (segment.scope === 'facebook' && facebook?.user?.adAccountId) {
      setFacebookTosLoading(true);
      fetch(
        `/api/admin/accounts/${accountId}/domains/${domainId}/facebook/customaudiences/tos?adAccountId=${facebook.user.adAccountId}`
      )
        .then(({ accepted, webCustomAudienceAccepted }) => {
          setFacebookTos({ accepted, webCustomAudienceAccepted });
        })
        .catch(setError)
        .finally(() => setFacebookTosLoading(false));
    }
  }, [fetch, accountId, domainId, segment.scope, facebook?.user?.adAccountId]);

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  const deleteButton = deleteSegment ? (
    <LockableButton restrictionKey="segmentsDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
      <IconTrash size={14} /> Delete
    </LockableButton>
  ) : undefined;

  const duplicateButton = onDuplicate ? (
    <CrossDomainCopy
      resourceType="Segment"
      resourceId={segment?.id}
      basePath="segments"
      restrictionKey="segmentsCreate"
      fallbackAction={onDuplicate}
    />
  ) : undefined;

  const rules = EXTERNAL_SCOPES.includes(segment.scope) ? (
    <AudienceRules segment={segment} updateSegment={setSegment} readOnly={!!segment.id} />
  ) : (
    <Grid align="flex-start" mb="sm" data-tour="segmentPageRules">
      <Grid.Col span={{ base: 12, sm: 2 }}>
        <Text fw={500}>Rules</Text>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 10 }}>
        <Rules
          rules={segment.rules}
          campaigns={campaigns}
          webhooks={webhooks}
          emails={emails}
          updateRules={(rules) => setSegment({ ...segment, rules })}
          availableCustomFields={segment.scope === 'profileAttributes' ? {} : customFields}
          eventTags={eventTags}
          eventActions={eventActions}
          subscriptions={subscriptions}
          pushNotifications={pushNotifications}
          textMessages={textMessages}
          viberMessages={viberMessages}
          changed={segment !== previousSegment}
          pages={pages}
          availableRules={segment.scope === 'profileFilter' ? profileFilterRules : behavioralRules}
          availableFilters={segment.scope === 'profileFilter' ? profileFilterRuleFilters : behavioralRuleFilters}
          segments={segments}
          whatsappMessages={whatsappMessages}
          surveys={surveys}
        />
        {segment.scope === 'behavior' && (
          <div style={{ padding: '10pt 0pt 0pt 0pt' }}>
            <Grid>
              <Grid.Col span={12}>
                <SegmentCounter
                  segment={segment}
                  showSample={true}
                  scope={countScope}
                  changed={JSON.stringify(segment) !== JSON.stringify(previousSegment)}
                  scopeControl={
                    <Group gap="xs" wrap="nowrap">
                      <Divider orientation="vertical" />
                      <Text size="sm" c="dimmed">
                        Marketing status
                      </Text>
                      <SegmentedControl
                        size="xs"
                        value={countScope}
                        onChange={setCountScope}
                        data-tour="segmentCountScope"
                        data={COUNT_SCOPES}
                      />
                    </Group>
                  }
                />
              </Grid.Col>
            </Grid>
          </div>
        )}
      </Grid.Col>
    </Grid>
  );

  return (
    <>
      <Grid align="center" mb="sm" ta="left">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text fw={500}>Name*</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <TextInput
            name="name"
            value={segment.name}
            placeholder="e.g. Idle Customers"
            onChange={(e) => setSegment({ ...segment, [e.target.name]: e.target.value })}
          />
        </Grid.Col>
      </Grid>
      <Grid align="center" mb="sm">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text fw={500}>Scope*</Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <SegmentedControl
            value={segment.scope ?? ''}
            disabled={!!segment.id}
            onChange={(scope) => {
              const modifiedSegment = JSON.parse(JSON.stringify(segment));
              modifiedSegment.scope = scope;
              if (EXTERNAL_SCOPES.includes(scope)) {
                modifiedSegment.externalScope = 'audienceRules';
                modifiedSegment.rules = [];
              } else {
                modifiedSegment.rules = [];
              }
              setSegment(modifiedSegment);
            }}
            data={[
              { label: 'Behavior', value: 'behavior' },
              { label: 'Profile filter', value: 'profileFilter' },
              { label: 'Facebook', value: 'facebook' },
              { label: 'Google', value: 'google' },
            ].filter((scope) => !disabledScopes.includes(scope.value))}
          />
        </Grid.Col>
      </Grid>

      {segment.scope === 'facebook' && facebook?.user?.adAccountId && (
        <>
          {!facebookTosLoading &&
            !facebookTos.webCustomAudienceAccepted &&
            segment.externalScope === 'audienceRules' && (
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 2 }} />
                <Grid.Col span={{ base: 12, sm: 10 }}>
                  <Text size="sm" c="red">
                    You need to accept Facebook Website Custom Audience Terms of Service{' '}
                    <a
                      href={`https://business.facebook.com/ads/manage/customaudiences/tos/?act=${facebook.user.adAccountId.replace(
                        'act_',
                        ''
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconExternalLink size={14} /> in the Business Manager
                    </a>
                    .
                  </Text>
                </Grid.Col>
              </Grid>
            )}
          {!facebookTosLoading &&
            !facebookTos.accepted &&
            (segment.externalScope === 'audienceLookalikes' || segment.externalScope === 'pageLikeLookalikes') && (
              <Grid align="center" mb="sm">
                <Grid.Col span={{ base: 12, sm: 2 }} />
                <Grid.Col span={{ base: 12, sm: 10 }}>
                  <Text size="sm" c="red">
                    You need to accept Facebook Custom Audience Terms of Service{' '}
                    <a
                      href={`https://business.facebook.com/ads/manage/customaudiences/tos/?act=${facebook.user.adAccountId.replace(
                        'act_',
                        ''
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconExternalLink size={14} /> in the Business Manager
                    </a>
                    .
                  </Text>
                </Grid.Col>
              </Grid>
            )}
        </>
      )}

      {rules}

      <Group justify="flex-end" gap="xs" py="md">
        <Button variant="default" onClick={onBack}>
          Back
        </Button>
        {deleteButton}
        {duplicateButton}
        <LockableButton
          onClick={() => saveSegment(segment)}
          restrictionKey={`segments${segment.id ? 'Update' : 'Create'}`}
          disabled={saveDisabled()}
          data-tour="segmentSaveBtn"
        >
          <IconDeviceFloppy size={14} /> Save
        </LockableButton>
        <ConfirmationModal
          error={deleteError}
          show={confirmDeleteOpen}
          close={() => setConfirmDeleteOpen(false)}
          confirm={(cb) => {
            setDeleteError(undefined);
            deleteSegment(cb).catch(setDeleteError);
          }}
          message="Deleting a segment will also delete all campaigns, workflows, recommenders, banners, and facebook product sets associated with it. Are you sure?"
        />
      </Group>
    </>
  );
};

export default SegmentForm;
